import { Component, signal, Input, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { APP_VERSION, APP_VERSION_FULL, APP_BUILD_ID, APP_RELEASE_DATE } from '../../version';

/**
 * Fixed bottom-right build badge → opens a dialog with version + release date,
 * and flags when the loaded PWA build is stale (an old cached bundle).
 *
 * Staleness: the bundle bakes in APP_BUILD_ID. We fetch the freshly-deployed
 * /assets/version.json (cache-busted, via native fetch to bypass the app's auth
 * interceptor). If the server buildId differs, this install is stale → show
 * hard-refresh instructions.
 */
interface VersionManifest {
  version: string;
  versionFull: string;
  buildId: string;
  releaseDate: string;
  angularMajor: number;
}

@Component({
  selector: 'app-version-badge',
  imports: [DatePipe, TranslateModule],
  template: `
    <button
      type="button"
      class="version-badge"
      [class.above-nav]="aboveNav"
      [class.stale]="isStale()"
      [attr.aria-label]="'version.showDetails' | translate"
      [title]="appVersionFull"
      (click)="open()"
    >{{ appVersion }}@if (isStale()) {&nbsp;⟳}</button>

    @if (dialogOpen()) {
      <div class="version-overlay" role="dialog" aria-modal="true" (click)="close()">
        <div class="version-dialog" (click)="$event.stopPropagation()">
          <div class="version-dialog__head">
            <h2>{{ 'version.title' | translate }}</h2>
            <button type="button" class="version-dialog__close" [attr.aria-label]="'common.close' | translate" (click)="close()">✕</button>
          </div>

          <dl class="version-dialog__list">
            <div><dt>{{ 'version.version' | translate }}</dt><dd class="mono">{{ appVersion }}</dd></div>
            <div><dt>{{ 'version.build' | translate }}</dt><dd class="mono">{{ appBuildId }}</dd></div>
            <div><dt>{{ 'version.released' | translate }}</dt><dd>{{ releaseDate | date:'medium' }}</dd></div>
          </dl>

          @if (isStale()) {
            <div class="version-update">
              <p class="version-update__title">{{ 'version.updateAvailable' | translate }}</p>
              @if (latest(); as l) {
                <p class="version-update__sub">{{ 'version.latestIs' | translate }} <span class="mono">{{ l.version }}</span>@if (l.releaseDate) { ({{ l.releaseDate | date:'medium' }}) }</p>
              }
              <p class="version-update__sub">{{ 'version.hardRefreshHow' | translate }}</p>
              <ul class="version-update__list">
                <li>{{ 'version.hardRefreshWin' | translate }}</li>
                <li>{{ 'version.hardRefreshMac' | translate }}</li>
                <li>{{ 'version.hardRefreshPwa' | translate }}</li>
              </ul>
              <button type="button" class="version-update__btn" (click)="reloadNow()">{{ 'version.reloadNow' | translate }}</button>
            </div>
          } @else if (checked()) {
            <p class="version-uptodate">{{ 'version.upToDate' | translate }}</p>
          }
        </div>
      </div>
    }
  `,
  styles: [
    `
      .version-badge {
        position: fixed;
        right: 6px;
        bottom: 4px;
        z-index: 999;
        user-select: none;
        cursor: pointer;
        font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
        font-size: 10px;
        line-height: 1;
        letter-spacing: -0.02em;
        color: var(--ink-soft, #5B5F63);
        background: var(--surface, rgba(255,255,255,0.7));
        border: 1px solid var(--line, rgba(23,26,30,0.12));
        border-radius: 6px;
        padding: 3px 6px;
        opacity: 0.85;
      }
      .version-badge.above-nav {
        bottom: calc(72px + env(safe-area-inset-bottom, 0px));
      }
      .version-badge.stale {
        color: #B45309;
        border-color: #F59E0B;
      }
      :host-context(.dark) .version-badge {
        color: var(--ink-soft, #9CA3AC);
        background: var(--surface, rgba(27,31,38,0.7));
        border-color: rgba(236,237,238,0.14);
      }
      :host-context(.dark) .version-badge.stale { color: #F5B759; border-color: #B4790E; }

      .version-overlay {
        position: fixed; inset: 0; z-index: 10000;
        background: rgba(0,0,0,0.5);
        display: flex; align-items: center; justify-content: center;
        padding: 16px;
      }
      .version-dialog {
        width: 100%; max-width: 360px;
        background: var(--surface, #FFFFFF);
        color: var(--ink, #171A1E);
        border: 1px solid var(--line, rgba(23,26,30,0.08));
        border-radius: 12px;
        box-shadow: 0 10px 30px rgba(0,0,0,0.2);
        padding: 18px;
      }
      :host-context(.dark) .version-dialog {
        background: var(--surface, #1B1F26);
        color: var(--ink, #ECEDEE);
        border-color: rgba(236,237,238,0.08);
      }
      .version-dialog__head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
      .version-dialog__head h2 { font-size: 16px; font-weight: 600; margin: 0; }
      .version-dialog__close {
        background: none; border: none; cursor: pointer; font-size: 16px;
        color: var(--ink-soft, #5B5F63); padding: 4px; line-height: 1;
      }
      :host-context(.dark) .version-dialog__close { color: var(--ink-soft, #9CA3AC); }
      .version-dialog__list { font-size: 14px; margin: 0; }
      .version-dialog__list > div { display: flex; justify-content: space-between; gap: 16px; padding: 4px 0; }
      .version-dialog__list dt { color: var(--ink-soft, #5B5F63); margin: 0; }
      :host-context(.dark) .version-dialog__list dt { color: var(--ink-soft, #9CA3AC); }
      .version-dialog__list dd { margin: 0; }
      .mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }

      .version-update {
        margin-top: 14px; padding: 12px;
        background: #FFF7ED; border: 1px solid #FED7AA; border-radius: 8px;
      }
      :host-context(.dark) .version-update { background: rgba(180,83,9,0.15); border-color: rgba(180,83,9,0.5); }
      .version-update__title { font-size: 14px; font-weight: 600; color: #9A3412; margin: 0 0 4px; }
      :host-context(.dark) .version-update__title { color: #F5B759; }
      .version-update__sub { font-size: 12px; color: #B45309; margin: 4px 0 0; }
      :host-context(.dark) .version-update__sub { color: #E8B15A; }
      .version-update__list { font-size: 12px; color: #B45309; margin: 4px 0 0; padding-left: 18px; }
      :host-context(.dark) .version-update__list { color: #E8B15A; }
      .version-update__btn {
        margin-top: 12px; width: 100%; padding: 8px 12px;
        font-size: 14px; font-weight: 600; color: #fff;
        background: #D97706; border: none; border-radius: 8px; cursor: pointer;
      }
      .version-update__btn:hover { background: #B45309; }
      .version-uptodate { margin-top: 14px; font-size: 12px; color: #2F6F5E; }
      :host-context(.dark) .version-uptodate { color: #4FA98D; }
    `,
  ],
})
export class VersionBadgeComponent implements OnInit {
  /** When true, raise the badge above the fixed bottom-nav. */
  @Input() aboveNav = false;

  readonly appVersion = APP_VERSION;
  readonly appVersionFull = APP_VERSION_FULL;
  readonly appBuildId = APP_BUILD_ID;
  readonly releaseDate = APP_RELEASE_DATE;

  readonly dialogOpen = signal(false);
  readonly isStale = signal(false);
  readonly checked = signal(false);
  readonly latest = signal<VersionManifest | null>(null);

  ngOnInit(): void {
    this.checkLatest();
  }

  open(): void {
    this.dialogOpen.set(true);
    this.checkLatest();
  }

  close(): void {
    this.dialogOpen.set(false);
  }

  reloadNow(): void {
    location.reload();
  }

  /**
   * Native fetch (not HttpClient) so the app's auth interceptor never attaches a
   * Bearer header or triggers a 401→logout on this static, cache-busted read.
   */
  private checkLatest(): void {
    fetch(`/assets/version.json?ts=${Date.now()}`, { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((m: VersionManifest | null) => {
        this.checked.set(true);
        if (m) {
          this.latest.set(m);
          this.isStale.set(!!m.buildId && m.buildId !== this.appBuildId);
        }
      })
      .catch(() => this.checked.set(true));
  }
}
